// ExistingPackageList.js
import React, { useState } from 'react';
import {
    Box,
    Grid,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Typography,
    IconButton,
    InputAdornment,
    Divider
} from '@mui/material';
import { ExpandMore, ExpandLess, } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomTypography from '../../../components/UIComponents/CustomTypography';
import theme from '../../../assets/theme';
import { fetchPackageDetails, updateRoomPackage } from '../../../service/network';
import { useNotification } from '../../../context/notification.context';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import StyledAutocomplete from '../../../components/UIComponents/StyledAutocomplete';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import moment from 'moment';
import CustomButton from '../../../components/UIComponents/CustomButton';
import childIcon from "../../../assets/icons/child.png"
import AdultIcon from "../../../assets/icons/adult.png"


export default function ExistingPackageList({
    packages,
    setPackages,
    roomTypeOptions,
    handleCopy,
    handleDelete,
    handleClose,
    toggleDay,
    toggleWeekend,
    toggleAllDays,
    handleToggleExpand
}) {
    const [loadingDetails, setLoadingDetails] = useState({});
    const [savingStates, setSavingStates] = useState({});
    const { showNotification } = useNotification();
    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weekends = ['Fri', 'Sat', 'Sun'];

    const handleEdit = (id) => {
        setPackages(prevPackages =>
            prevPackages.map(item => {
                if (item.id === id) {
                    if (item.isExpanded) {
                        return { ...item, isEditing: true };
                    } else {
                        showNotification('warning', 'Action Blocked', 'Please expand the package before editing.');
                        return item;
                    }
                }
                return item;
            })
        );
    };
    const CODE_WIDTH = 150;
    const NAME_WIDTH = 200;
    const STATUS_WIDTH = 120;

    const handleAddRoomType = (packageId) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id === packageId) {
                    // Check if there's any empty room type first
                    const hasEmptyRoomType = pkg.packageDetails.some(
                        detail => !detail.roomType ||
                            !detail.fromDate ||
                            !detail.toDate ||
                            !detail.baseRate ||
                            detail.selectedDays.length === 0
                    );

                    if (hasEmptyRoomType) {
                        showNotification('warning', 'Action Blocked', 'Please fill in all required fields for existing room types before adding a new one.');
                        return pkg;
                    }

                    // Check for maximum room types limit (optional)
                    if (pkg.packageDetails.length >= 10) { // Example limit
                        showNotification('warning', 'Maximum Limit Reached', 'You can only add up to 10 room types per package.');
                        return pkg;
                    }

                    const newRoomType = {
                        name: `new_${Date.now()}`,
                        roomType: '',
                        fromDate: moment().format('YYYY-MM-DD'),
                        toDate: moment().add(1, 'year').format('YYYY-MM-DD'),
                        selectedDays: [],
                        baseRate: '',
                        adults: '2', // Default value
                        children: '0', // Default value
                        extraAdultCharge: '',
                        extraChildCharge: '',
                        weekendSurcharge: '',
                        surchargeType: '₹',
                        selectedWeekends: [],
                        isNew: true,
                        // ERPNext specific fields
                        parent: pkg.name,
                        parentfield: "package_details",
                        parenttype: "Room Package",
                        doctype: "Package Details",
                        // Additional metadata
                        __islocal: true, // Marks as unsaved in Frappe/ERPNext
                        __unsaved: 1     // Additional flag for unsaved
                    };

                    return {
                        ...pkg,
                        packageDetails: [...pkg.packageDetails, newRoomType]
                    };
                }
                return pkg;
            })
        );
    };
    const handleRemoveRoomType = (packageId, detailIndex) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id === packageId) {
                    const detailToRemove = pkg.packageDetails[detailIndex];

                    // More strict check for truly new unsaved room types
                    const isTrulyNewUnsaved = detailToRemove.isNew ||
                        detailToRemove.__islocal ||
                        detailToRemove.__unsaved ||
                        detailToRemove.name?.startsWith('new_');

                    if (!isTrulyNewUnsaved) {
                        showNotification(
                            'error',
                            'Cannot Remove',
                            'Only newly added (unsaved) room types can be removed.'
                        );
                        return pkg;
                    }

                    const updatedDetails = [...pkg.packageDetails];
                    updatedDetails.splice(detailIndex, 1);

                    return {
                        ...pkg,
                        packageDetails: updatedDetails
                    };
                }
                return pkg;
            })
        );
    };
    const allowOnlyPositiveNumbers = (value) => {
        // Allow only digits (0–9), optional decimal
        return value === '' || /^\d*\.?\d*$/.test(value);
    };

    const handleSave = async (id) => {
        const pkg = packages.find(p => p.id === id);
        if (!pkg) return;

        // Check for duplicate room types before saving
        const roomTypes = pkg.packageDetails.map(detail => detail.roomType);
        const uniqueRoomTypes = new Set(roomTypes);

        if (roomTypes.length !== uniqueRoomTypes.size) {
            showNotification('error', 'Duplicate Room Types', 'Each room type in a package must be unique.');
            return;
        }

        // Check for empty required fields
        const hasEmptyFields = pkg.packageDetails.some(detail =>
            !detail.roomType ||
            !detail.fromDate ||
            !detail.toDate ||
            !detail.baseRate ||
            detail.selectedDays.length === 0
        );

        if (hasEmptyFields) {
            showNotification('error', 'Missing Required Fields', 'Please fill all required fields for each room type.');
            return;
        }

        setSavingStates(prev => ({ ...prev, [id]: true }));

        try {
            // Prepare the package data for API
            const packageData = {
                name: pkg.name,
                package_name: pkg.packageName,
                package_code: pkg.packageCode,
                status: pkg.status,
                // company: "Mathiro Bliss", // Add company if required
                doctype: "Room Package",
                package_details: pkg.packageDetails.map(detail => ({
                    ...(detail.name && !detail.name.startsWith('new_') ? { name: detail.name } : {}),
                    room_type: detail.roomType,
                    from_date: detail.fromDate,
                    to_date: detail.toDate,
                    applicable_days: JSON.stringify(detail.selectedDays.map(day => day.toLowerCase())),
                    rate_per_night: detail.baseRate,
                    no_of_adult: detail.adults,
                    no_of_child: detail.children,
                    extra_adult_charge: detail.extraAdultCharge,
                    extra_child_charge: detail.extraChildCharge,
                    surcharge: detail.weekendSurcharge,
                    surcharge_method: detail.surchargeType,
                    weekend_days: JSON.stringify(detail.selectedWeekends.map(day => day.toLowerCase())),
                    parent: pkg.name,
                    parentfield: "package_details",
                    parenttype: "Room Package",
                    doctype: "Package Details"
                }))
            };

            // Make the API call to update the package
            const response = await updateRoomPackage(pkg.name, packageData);

            // Update the package with the response data if needed
            setPackages(prevPackages =>
                prevPackages.map(item =>
                    item.id === id ? {
                        ...item,
                        isEditing: false,
                        packageDetails: item.packageDetails.map(detail => ({
                            ...detail,
                            // Clear all flags that mark this as a new room type
                            isNew: false,
                            __islocal: false,
                            __unsaved: false,
                            // Update name if it was a temporary one
                            name: detail.name.startsWith('new_')
                                ? response.data.data.package_details.find(d =>
                                    d.room_type === detail.roomType)?.name || detail.name
                                : detail.name
                        }))
                    } : item
                )
            );

            showNotification('success', 'Success', 'Package updated successfully.');
        } catch (error) {
            console.error('Error updating package:', error);
            let errorMessage = error.message || 'Failed to update package.';

            if (error.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(error.response.data._server_messages)
                        .map(msg => JSON.parse(msg).message.replace(/<[^>]+>/g, ''))
                        .join(', ');
                    errorMessage = messages;
                } catch (parseErr) {
                    console.error('Error parsing server messages:', parseErr);
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            showNotification('error', 'Error', errorMessage);
        } finally {
            setSavingStates(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleExpand = async (id) => {
        const pkg = packages.find(p => p.id === id);
        const isExpanding = !pkg.isExpanded;

        handleToggleExpand(id);

        if (!isExpanding) return;

        if (!pkg.hasDetails) {
            setLoadingDetails(prev => ({ ...prev, [id]: true }));

            try {
                const response = await fetchPackageDetails(pkg.name);

                if (!response.data || !response.data.data || !response.data.data.package_details) {
                    throw new Error('Invalid package details response');
                }

                const parseDays = (daysString) => {
                    try {
                        if (!daysString) return [];
                        const cleaned = daysString
                            .replace(/^\[|\]$/g, '')
                            .replace(/"/g, '')
                            .replace(/'/g, '')
                            .toLowerCase();

                        return cleaned.split(',')
                            .map(day => day.trim())
                            .map(day => day.charAt(0).toUpperCase() + day.slice(1));
                    } catch (error) {
                        console.error('Error parsing days:', error);
                        return [];
                    }
                };

                const formatDate = (dateString) => {
                    return dateString ? dateString.split(' ')[0] : '';
                };

                const packageDetails = response.data.data.package_details.map(detail => ({
                    name: detail.name,
                    roomType: detail.room_type || '',
                    fromDate: formatDate(detail.from_date),
                    toDate: formatDate(detail.to_date),
                    selectedDays: parseDays(detail.applicable_days),
                    baseRate: detail.rate_per_night || '',
                    adults: detail.no_of_adult || '',
                    children: detail.no_of_child || '',
                    extraAdultCharge: detail.extra_adult_charge || '',
                    extraChildCharge: detail.extra_child_charge || '',
                    weekendSurcharge: detail.surcharge || '',
                    surchargeType: detail.surcharge_method || '$',
                    selectedWeekends: parseDays(detail.weekend_days)
                }));

                setPackages(prevPackages =>
                    prevPackages.map(item => {
                        if (item.id === id) {
                            return {
                                ...item,
                                hasDetails: true,
                                packageDetails: packageDetails
                            };
                        }
                        return item;
                    })
                );
            } catch (error) {
                console.error('Failed to fetch package details:', error);
                let errorMessage = error.message || 'Failed to fetch package details.';

                if (error.response?.data?._server_messages) {
                    try {
                        const messages = JSON.parse(error.response.data._server_messages)
                            .map(msg => JSON.parse(msg).message.replace(/<[^>]+>/g, ''))
                            .join(', ');
                        errorMessage = messages;
                    } catch (parseErr) {
                        console.error('Error parsing server messages:', parseErr);
                    }
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }

                showNotification('error', 'Error', errorMessage);
            } finally {
                setLoadingDetails(prev => ({ ...prev, [id]: false }));
            }
        }
    };
    const handlePackageChange = (field, value, packageId, detailIndex) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id === packageId) {
                    // Check for duplicate room types when changing room type
                    if (field === 'roomType') {
                        const isDuplicate = pkg.packageDetails.some(
                            (detail, idx) => idx !== detailIndex && detail.roomType === value
                        );

                        if (isDuplicate) {
                            showNotification('error', 'Duplicate Room Type', 'This room type already exists in the package.');
                            return pkg; // Don't update if duplicate
                        }
                    }

                    const updatedDetails = pkg.packageDetails.map((detail, idx) => {
                        if (idx === detailIndex) {
                            return { ...detail, [field]: value };
                        }
                        return detail;
                    });

                    return { ...pkg, packageDetails: updatedDetails };
                }
                return pkg;
            })
        );
    };
    // Add this function with your other handler functions
    const handlePackageInfoChange = (field, value, packageId) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id === packageId) {
                    return { ...pkg, [field]: value };
                }
                return pkg;
            })
        );
    };

    const renderPackageDetail = (detail, pkg, index) => {
        const isRemovableRoom = (
            detail.isNew ||
            detail.__isLocal ||
            detail.__unsaved ||
            (detail.name && detail.name.startsWith('new_'))
        );
        return (
            <>
                <Box
                    key={index}
                    sx={{
                        border: '1px solid #CFCFCF',
                        borderRadius: theme.borderRadius.large,
                        p: 2,
                        mt: 2,
                        backgroundColor: 'light.main',
                        position: 'relative',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {/* Close icon for new room types in edit mode */}
                    {pkg.isEditing && isRemovableRoom && (
                        <IconButton
                            onClick={() => handleRemoveRoomType(pkg.id, index)}
                            sx={{
                                position: 'absolute',
                                right: -8,
                                top: -8,
                                color: 'error.contrastText',
                                backgroundColor: 'error.main',
                                width: 20,
                                height: 20,
                                '&:hover': {
                                    backgroundColor: 'error.dark',
                                },
                                zIndex: 2
                            }}
                            size="small"
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    )}

                    {/* "NEW" badge - only for removable rooms */}
                    {isRemovableRoom && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: 10,
                                backgroundColor: 'primary.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                zIndex: 1,
                                boxShadow: 1
                            }}
                        >
                            NEW
                        </Box>
                    )}

                    {/* Main Content Grid */}
                    <Grid container spacing={2} alignItems="center">
                        {/* Room Type Field */}
                        <Grid size={2}>
                            <StyledAutocomplete
                                options={roomTypeOptions}
                                label="Room Type"
                                value={detail.roomType || ''}
                                onChange={(e, newValue) => handlePackageChange('roomType', newValue, pkg.id, index)}
                                size="small"
                                disableClearable
                                disabled={!pkg.isEditing}
                                renderInput={(params) => (
                                    <StyledTextField
                                        {...params}
                                        placeholder="Select"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Validity Period Fields */}
                        <Grid size={1.5}>
                            <StyledTextField
                                label="Validity Period"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={detail.fromDate}
                                onChange={(e) => handlePackageChange('fromDate', e.target.value, pkg.id, index)}
                                disabled={!pkg.isEditing}
                                error={detail.toDate && detail.fromDate > detail.toDate}

                            />
                        </Grid>

                        <Grid size={1.5} mt={2.8}>
                            <StyledTextField
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={detail.toDate}
                                onChange={(e) => handlePackageChange('toDate', e.target.value, pkg.id, index)}
                                disabled={!pkg.isEditing}
                                InputProps={{
                                    inputProps: {
                                        min: detail.fromDate || moment().format("YYYY-MM-DD"),
                                    },
                                }}
                                error={detail.fromDate && detail.toDate < detail.fromDate}

                            />
                        </Grid>


                        {/* Applicable Days */}
                        <Grid size={5}>
                            <Box>
                                <CustomTypography
                                    variant="bodyText1"
                                    display="block"
                                    gutterBottom
                                    color="dark.main"
                                >
                                    Applicable Days
                                </CustomTypography>

                                <Box
                                    display="flex"
                                    flexWrap={{ xs: "wrap", md: "nowrap" }} // Wrap on small screens, no-wrap on medium+
                                    gap={1}
                                    sx={{
                                        border: '1px solid #CFCFCF',
                                        borderRadius: 2,
                                        backgroundColor: 'light.main',
                                        pl: 1,
                                        height: 40,
                                        overflowX: 'auto', // Horizontal scrolling when needed
                                        overflowY: 'hidden', // Never show vertical scrollbar
                                        '&::-webkit-scrollbar': {
                                            height: '8px', // Thinner scrollbar
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#CFCFCF',
                                            borderRadius: '3px',
                                        },
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={detail.selectedDays.length === 7}
                                                onChange={() => toggleAllDays(pkg.id, index)}
                                                disabled={!pkg.isEditing}
                                            />
                                        }
                                        label="All"
                                        sx={{
                                            flex: { xs: '0 0 50%', sm: '0 0 33.33%', md: '0 0 auto' },
                                            mr: 1,
                                            minWidth: 'fit-content', // Prevent shrinking
                                        }}
                                    />

                                    {daysOfWeek.map((day) => (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox
                                                    checked={detail.selectedDays.includes(day)}
                                                    onChange={() => toggleDay(day, pkg.id, index)}
                                                    disabled={!pkg.isEditing}
                                                />
                                            }
                                            label={day}
                                            sx={{
                                                flex: { xs: '0 0 50%', sm: '0 0 33.33%', md: '0 0 auto' },
                                                m: 0,
                                                minWidth: 'fit-content', // Prevent shrinking
                                            }}
                                        />
                                    ))}
                                </Box>

                            </Box>
                        </Grid>

                        {/* Base Rate */}
                        <Grid size={2}>
                            <StyledTextField
                                label="Base Rate / Night"
                                placeholder="Enter Amount"
                                value={detail.baseRate}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handlePackageChange('baseRate', val, pkg.id, index);
                                    }
                                }}
                                disabled={!pkg.isEditing}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                    inputProps: {
                                        style: { textAlign: 'right' }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Occupancy Limit */}
                        <Grid size={1.5}>
                            <Typography variant='bodyText1' color='dark.main'>Occupancy Limit</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <img src={AdultIcon} width={22} alt="Adults" />
                                <StyledTextField
                                    value={detail.adults}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val, false)) {
                                            handlePackageChange('adults', val, pkg.id, index);
                                        }
                                    }}
                                    disabled={!pkg.isEditing}
                                    inputProps={{ min: 1, max: 10 }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={1.5}>
                            <Box display="flex" alignItems="center" gap={1} mt={2.8} ml={-1.2}>
                                <img src={childIcon} width={22} alt="Children" />
                                <StyledTextField
                                    value={detail.children}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val, false)) {
                                            handlePackageChange('children', val, pkg.id, index);
                                        }
                                    }}
                                    disabled={!pkg.isEditing}
                                    inputProps={{ min: 0, max: 10 }}
                                />
                            </Box>
                        </Grid>

                        {/* Extra Charges */}
                        <Grid size={1.5}>
                            <StyledTextField
                                label="Extra Adult Charge"
                                placeholder='Enter Amount'
                                value={detail.extraAdultCharge}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handlePackageChange('extraAdultCharge', val, pkg.id, index);
                                    }
                                }}
                                disabled={!pkg.isEditing}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={1.5}>
                            <StyledTextField
                                label="Extra Child Charge"
                                placeholder='Enter Amount'
                                value={detail.extraChildCharge}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handlePackageChange('extraChildCharge', val, pkg.id, index);
                                    }
                                }}
                                disabled={!pkg.isEditing}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Weekend Surcharge */}
                        <Grid size={1.5}>
                            <Box>
                                <StyledTextField
                                    fullWidth
                                    label="Weekend Surcharge"
                                    placeholder='Enter Amount'
                                    size="small"
                                    value={detail.weekendSurcharge}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val)) {
                                            handlePackageChange('weekendSurcharge', val, pkg.id, index);
                                        }
                                    }}
                                    disabled={!pkg.isEditing}
                                />
                            </Box>
                        </Grid>
                        <Grid size={0.8} mt={3} ml={-1}>
                            <StyledAutocomplete
                                options={['%', '₹']}
                                value={detail.surchargeType}
                                onChange={(e, newValue) => handlePackageChange('surchargeType', newValue, pkg.id, index)}
                                size="small"
                                disableClearable
                                disabled={!pkg.isEditing}
                                renderInput={(params) => (
                                    <StyledTextField {...params} placeholder='$' />
                                )}
                            />
                        </Grid>

                        {/* Choose Weekend */}
                        <Grid size={1.5}>
                            <Box>
                                <CustomTypography variant="bodyText1" display="block" gutterBottom color="dark.main">
                                    Choose Weekend
                                </CustomTypography>
                                <Box display="flex" gap={1}
                                    sx={{
                                        border: '1px solid #CFCFCF',
                                        borderRadius: 2,
                                        backgroundColor: 'light.main',
                                        minWidth: 260,
                                        height: 40,
                                        pl: 1,
                                    }}>
                                    {weekends.map((day) => (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox
                                                    checked={detail.selectedWeekends.includes(day)}
                                                    onChange={() => toggleWeekend(day, pkg.id, index)}
                                                    disabled={!pkg.isEditing}
                                                />
                                            }
                                            label={day}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Add Room Type Button */}
                {index === pkg.packageDetails.length - 1 && pkg.isExpanded && pkg.isEditing && (
                    <Box mt={2} display="flex" justifyContent="flex-start">
                        <CustomButton
                            variant="outlined"
                            onClick={() => handleAddRoomType(pkg.id)}
                            sx={{
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                borderRadius: theme.borderRadius.medium,
                                border: "2px solid",
                                textTransform: 'none',
                            }}
                            startIcon={<AddIcon />}
                        >
                            Add Room Type
                        </CustomButton>
                    </Box>
                )}
            </>
        );
    };

    return (
        <Box>
            {packages.map((pkg) => (
                <Box
                    key={pkg.id}
                    sx={{
                        backgroundColor: theme.palette.light.main,
                        borderRadius: theme.borderRadius.xl,
                        boxShadow: '0px 4px 12px 0px rgba(74, 76, 78, 0.25)',
                        width: '100%',
                        maxHeight: "fit-content",
                        p: 2.5,
                        mt: 2,
                        backgroundColor: 'light.main',
                        position: 'relative'
                    }}
                >


                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {/* LEFT SECTION */}
                        <Box display="flex" alignItems="center" gap={2}>
                            {/* Package Code - becomes editable text field in edit mode */}
                            {pkg.isEditing ? (
                                <StyledTextField
                                    sx={{ width: CODE_WIDTH }}
                                    label="Package Code"
                                    placeholder="Enter the Code"
                                    value={pkg.packageCode}
                                    onChange={(e) => handlePackageInfoChange('packageCode', e.target.value, pkg.id)}
                                />
                            ) : (
                                <Typography
                                    variant="SubTitleBold"
                                    sx={{
                                        width: CODE_WIDTH,
                                        textTransform: 'uppercase',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: "primary.main"
                                    }}
                                >
                                    {pkg.packageCode}
                                </Typography>
                            )}


                            {/* Divider */}
                            {/* Divider only in saved (non-edit) mode */}
                            {!pkg.isEditing && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        borderRightWidth: 2,
                                        height: 24,
                                        borderColor: '#27394D',
                                        alignSelf: 'center',

                                    }}
                                />
                            )}


                            {/* Package Name - becomes editable text field in edit mode */}
                            {pkg.isEditing ? (
                                <StyledTextField
                                    sx={{ width: NAME_WIDTH }}
                                    label="Package Name"
                                    placeholder="Enter the Name"
                                    value={pkg.packageName}
                                    onChange={(e) => handlePackageInfoChange('packageName', e.target.value, pkg.id)}
                                />
                            ) : (
                                <Typography
                                    variant="SubTitleBold"
                                    sx={{
                                        width: NAME_WIDTH,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: 'primary.main',
                                    }}
                                >
                                    {pkg.packageName}
                                </Typography>
                            )}



                            {/* Status - becomes dropdown in edit mode */}
                            {pkg.isEditing ? (
                                <StyledAutocomplete
                                    options={['Active', 'Inactive']}
                                    value={pkg.status}
                                    label="Status"
                                    onChange={(e, newValue) => handlePackageInfoChange('status', newValue, pkg.id)}
                                    size="small"
                                    disableClearable
                                    sx={{ width: STATUS_WIDTH }}
                                    renderInput={(params) => (
                                        <StyledTextField {...params} />
                                    )}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: STATUS_WIDTH,
                                        px: 2,
                                        py: 0.5,
                                        backgroundColor: pkg.status === 'Active' ? 'green.main' : 'error.main',
                                        color: 'white',
                                        textAlign: 'center',
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        pointerEvents: 'none',
                                        userSelect: 'none',
                                    }}
                                >
                                    <Typography variant="bodyText1" fontWeight="bold">
                                        {pkg.status}
                                    </Typography>
                                </Box>
                            )}


                            {/* Expand / Collapse Icon */}
                            <IconButton
                                onClick={() => handleExpand(pkg.id)}
                                sx={{
                                    backgroundColor: 'lightblue.main',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    border: "1px solid #84AFDF", // ✅ Correct spelling
                                    display: 'flex',
                                    alignItems: 'center',
                                    ...(pkg.isEditing && { mt: 3 }),

                                    justifyContent: 'center',
                                    ml: 1,

                                    '&:hover': {
                                        backgroundColor: 'lightblue.dark',
                                    },
                                }}
                            >
                                {loadingDetails[pkg.id] ? (
                                    <CircularProgress size={20} />
                                ) : pkg.isExpanded ? (
                                    <ExpandLess sx={{ color: "primary.main" }} />
                                ) : (
                                    <ExpandMore sx={{ color: 'primary.main' }} />
                                )}
                            </IconButton>
                        </Box>

                        {/* RIGHT SECTION (keep this part the same) */}
                        <Box display="flex" gap={2}>
                            {!pkg.isEditing && pkg.isExpanded && (
                                <>
                                    <IconButton
                                        onClick={() => handleEdit(pkg.id)}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '4px',
                                            padding: '2px',
                                            border: '1px solid #84AFDF',
                                            backgroundColor: "lightblue.main",
                                        }}
                                    >
                                        <ModeEditOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                    </IconButton>

                                    <IconButton
                                        onClick={() => handleCopy(pkg.id)}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '4px',
                                            padding: '2px',
                                            border: '1px solid #84AFDF',
                                            backgroundColor: "lightblue.main",
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                    </IconButton>
                                </>
                            )}

                            {pkg.isEditing && (
                                <IconButton
                                    onClick={() => handleSave(pkg.id)}
                                    disabled={savingStates[pkg.id]}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '4px',
                                        padding: '2px',
                                        border: '1px solid #84AFDF',
                                        backgroundColor: 'lightblue.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {savingStates[pkg.id] ? (
                                        <CircularProgress size={18} color="inherit" />
                                    ) : (
                                        <SaveOutlinedIcon sx={{ color: 'primary.main', width: 24, }} />
                                    )}
                                </IconButton>
                            )}
                            {pkg.isEditing ? (
                                <IconButton
                                    onClick={() => handleClose(pkg.id)}
                                    sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: '4px',
                                        padding: '2px',
                                        border: '1px solid #84AFDF',
                                        backgroundColor: 'lightblue.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CloseRoundedIcon sx={{ width: 22, color: 'text.primary' }} />
                                </IconButton>
                            ) : ""}
                        </Box>
                    </Box>

                    {pkg.isExpanded && pkg.packageDetails.map((detail, index) => (
                        renderPackageDetail(detail, pkg, index)
                    ))}
                </Box>
            ))}
        </Box>
    );
}